import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';

const WorldChatTypingIndicator = forwardRef(({ token }, ref) => {
  const [typingUsers, setTypingUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/api/ws/world-chat/typing`);

    ws.onopen = () => {
      console.log('World Chat Typing WebSocket connected');
      setIsConnected(true);
      
      // Request current typing users
      ws.send(JSON.stringify({ type: 'get_typing_users', token }));
      
      // Ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', token }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'typing_update') {
        setTypingUsers(data.typing_users || []);
      }
    };

    ws.onclose = () => {
      console.log('World Chat Typing WebSocket disconnected');
      setIsConnected(false);
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => connectWebSocket(), 3000);
    };

    ws.onerror = (error) => {
      console.error('World Chat Typing WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [WS_URL, token]);

  useEffect(() => {
    if (token) {
      connectWebSocket();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket, token]);

  // Notify typing start with debounce
  const notifyTypingStart = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing_start', token }));
      
      // Auto-stop after 5 seconds of no typing
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing_stop', token }));
        }
      }, 5000);
    }
  }, [token]);

  const notifyTypingStop = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing_stop', token }));
    }
  }, [token]);

  // Expose methods via useImperativeHandle
  useImperativeHandle(ref, () => ({
    notifyTypingStart,
    notifyTypingStop
  }), [notifyTypingStart, notifyTypingStop]);

  if (typingUsers.length === 0) {
    return null;
  }

  return (
    <div className="px-4 py-3 animate-fadeIn" data-testid="world-chat-typing-indicator">
      <div className="max-w-full md:max-w-2xl md:mx-auto">
        <div className="inline-flex items-center bg-gradient-to-r from-purple-900/80 to-indigo-900/80 backdrop-blur-sm rounded-2xl px-5 py-3 border border-purple-500/30 shadow-xl shadow-purple-500/10">
          {/* Animated avatars */}
          <div className="flex -space-x-3 mr-4">
            {typingUsers.slice(0, 4).map((user, idx) => (
              <div 
                key={user.user_id}
                className="relative"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center border-2 border-gray-900 shadow-lg transform hover:scale-110 transition-transform">
                  {user.avatar_url ? (
                    <img 
                      src={user.avatar_url} 
                      alt={user.nickname}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-sm font-bold">
                      {user.nickname?.charAt(0).toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                {/* Pulsing ring */}
                <div className="absolute inset-0 rounded-full bg-purple-400/40 animate-ping"></div>
              </div>
            ))}
            {typingUsers.length > 4 && (
              <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center border-2 border-gray-900 text-white text-xs font-bold">
                +{typingUsers.length - 4}
              </div>
            )}
          </div>
          
          {/* Writing animation */}
          <div className="flex items-center mr-3">
            <div className="flex space-x-1">
              <div className="w-2.5 h-2.5 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-typingWave" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2.5 h-2.5 bg-gradient-to-r from-pink-400 to-red-400 rounded-full animate-typingWave" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2.5 h-2.5 bg-gradient-to-r from-red-400 to-orange-400 rounded-full animate-typingWave" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
          
          {/* Text */}
          <div className="flex flex-col">
            <span className="text-white font-medium text-sm">
              {typingUsers.length === 1 ? (
                <><span className="text-purple-300">{typingUsers[0].nickname}</span> scrie o postare</>
              ) : typingUsers.length === 2 ? (
                <><span className="text-purple-300">{typingUsers[0].nickname}</span> și <span className="text-purple-300">{typingUsers[1].nickname}</span> scriu</>
              ) : (
                <><span className="text-purple-300">{typingUsers.length}</span> persoane scriu postări</>
              )}
            </span>
            <span className="text-gray-400 text-xs">în World Chat...</span>
          </div>
          
          {/* Animated pen icon */}
          <div className="ml-3 animate-bounce">
            <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
});

export default WorldChatTypingIndicator;
